import React from 'react'
import { useTranslation } from '../../providers/translation'
import { useAppContext, User } from '../../providers/context'
import { ThreePanelLayout } from '../../layouts/threePanel'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { DEFAULT_FILTERS, Filters, FilterSearch } from './filters'
import { InboxTabs, InboxTab, UnreadCounts } from './inboxes'
import { ContactGroup, Contacts, ContactsData, FilteredContacts } from './contacts'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import { Messages } from './messages'
import {
  ComposeState,
  DEFAULT_COMPOSE_STATE,
  SendType,
  SharedRecipient,
  StudentRecipient,
  TeacherRecipient,
} from './compose/composeTypes'
import { Conversation, ConversationRef } from './conversation/conversation'
import { NoConversationTips } from './noConversationTips'
import { deleteGroup, EditGroupModal, EditGroupState, NEW_GROUP_STATE } from './editGroup'
import { useDebounceLeading, useIsMobile, useDebounce } from '../../hooks/hooks'
import { ComposeModal } from './compose/compose'
import { tryParseInt } from '../../utils'
import { draftToComposeState } from './compose/composeHelpers'
import { useTermsOfService } from '../modals/termsOfService'
import { ConferenceInvites, Conferences } from './conferences'
import { Surveys } from '../survey/survey'
import { usePusher } from '../../providers/pusher'
import { useNavigate } from 'react-router-dom'
import { URLAction, useURLParams } from '../../providers/URLParams'
import { MessageData } from './message'
import { IconFilter } from '../../icons/library'
import { BasicUser } from '../../components/userSelector/users'
import { useHashTab } from '../../layouts/components/tabs'
import { AttendanceMessages } from './attendanceMessages'
import { HomeOnboarding } from './homeOnboarding'
import { Assessments } from '../assessments/assessments'

interface HomePageProps {
  user: User
}

export const HomePage: React.FC<HomePageProps> = ({ user }) => {
  const { state } = useAppContext()
  const { tr, changeLanguage } = useTranslation()
  const URLParams = useURLParams()

  // only read the hash tab once, on first load, for simplicity
  const [savedInboxTab, setSavedInboxTab] = useHashTab(InboxTab.Inbox, Object.values(InboxTab))
  const [inboxTab, setInboxTabState] = React.useState<{ tab: InboxTab }>({ tab: savedInboxTab as InboxTab })
  // keep the saved tab in sync with the current tab
  React.useEffect(() => {
    setSavedInboxTab(inboxTab.tab)
  }, [inboxTab.tab])

  const [conversationRequest, setConversationRequest] = React.useState<
    { messageID?: number; messageUserID?: number } | undefined
  >(undefined)
  const conversationRef = React.useRef<ConversationRef>(null)
  const [composeState, setComposeState] = React.useState<ComposeState | undefined>(undefined)
  const [editGroupState, setEditGroupState] = React.useState<EditGroupState | undefined>(undefined)
  const [filters, setFilters] = React.useState<Filters>({
    ...DEFAULT_FILTERS,
    thisYearsMessages: user.hasMessagesFromLastYear,
  })
  const [filteredContacts, setFilteredContacts] = React.useState<FilteredContacts>({
    groups: new Set(),
    schools: new Set(),
    students: new Set(),
    teachers: new Set(),
  })

  // When filters change in a way that might make a conversation no longer visible, clear the conversation
  React.useEffect(() => {
    if (!filters.showArchived) setConversationRequest(undefined)
  }, [filters.showArchived])
  React.useEffect(() => {
    if (!filters.allSchoolMessages) setConversationRequest(undefined)
  }, [filters.allSchoolMessages])

  const { data: contacts, reload: reloadContacts } = useRequest<ContactsData>('/frontend_api/getContacts', undefined)

  const [unreadCounts, setUnreadCounts] = React.useState<UnreadCounts | undefined>(undefined)
  // inital load occurs on message load success
  const reloadUnreadCounts = useDebounce(
    React.useCallback(() => {
      makeRequest<UnreadCounts>('/frontend_api/unreadCounts', {
        thisYearsMessages: filters.thisYearsMessages,
      })
        .then(setUnreadCounts)
        .catch(showAxiosErrorAsToast)
    }, [filters.thisYearsMessages]),
    500
  )

  React.useEffect(() => {
    if (URLParams.has(URLAction.OpenMessageUserConversation)) {
      const id = parseInt(URLParams.pop(URLAction.OpenMessageUserConversation)!)
      if (!isNaN(id)) setConversationRequest({ messageUserID: id })
    } else if (URLParams.has(URLAction.OpenMessageConversation)) {
      const id = parseInt(URLParams.pop(URLAction.OpenMessageConversation)!)
      if (!isNaN(id)) setConversationRequest({ messageID: id })
    }
    if (URLParams.has(URLAction.OpenInboxTab)) {
      const tab = URLParams.pop(URLAction.OpenInboxTab) as InboxTab
      if (Object.values(InboxTab).includes(tab)) setInboxTabState({ tab })
    }
    if (URLParams.has(URLAction.SetSearch)) {
      const search = URLParams.pop(URLAction.SetSearch)
      setFilters((f) => ({ ...f, search: search || f.search }))
    }
    if (URLParams.has(URLAction.SetSearchUser)) {
      const searchUser = JSON.parse(URLParams.pop(URLAction.SetSearchUser)!) as BasicUser
      setFilters((f) => ({
        ...f,
        searchUsers: f.searchUsers.concat(searchUser),
      }))
    }
    if (URLParams.has(URLAction.SetAllSchoolMessages)) {
      const allSchoolMessages =
        URLParams.pop(URLAction.SetAllSchoolMessages) === 'true' && user.type === 'teacher' && user.admin > 0
      setFilters((f) => ({ ...f, allSchoolMessages }))
    }
    if (URLParams.pop(URLAction.OpenCompose)) {
      const users = (
        user.type === 'teacher'
          ? URLParams.pop(URLAction.SetComposeStudents) || ''
          : URLParams.pop(URLAction.SetComposeTeachers) || ''
      )
        .split(',')
        .filter((s) => !!s)
        .map(parseInt)
      const groups = (URLParams.pop(URLAction.SetComposeGroups) || '')
        .split(',')
        .filter((s) => !!s)
        .map(tryParseInt) // group ids can be strings
      const individuals = (URLParams.pop(URLAction.SetComposeIndividuals) || '')
        .split(',')
        .filter((s) => !!s)
        .map(parseInt)

      const regardingStudentStr = URLParams.pop(URLAction.SetComposeRegardingStudent) || ''
      const regardingStudentID = regardingStudentStr ? parseInt(regardingStudentStr) : undefined
      const regardingStudent =
        regardingStudentID && user.type === 'guardian'
          ? user.children.find((c) => c.id === regardingStudentID)
          : undefined

      const sendTypes = URLParams.has(URLAction.SetComposeSendType)
        ? new Set(
            (URLParams.pop(URLAction.SetComposeSendType) || '')
              .split(',')
              .filter((s) => ['email', 'text', 'phone', 'mass'].includes(s)) as SendType[]
          )
        : DEFAULT_COMPOSE_STATE.sendTypes
      setComposeStateAfterTOS({
        ...DEFAULT_COMPOSE_STATE,
        sendTypes,
        regardingStudent,
        initiallySelectedRecipients: {
          users,
          groups,
          individuals,
        },
      })
    }
  }, [URLParams])

  const setInboxTab = React.useCallback((tab: InboxTab) => {
    // this wrappers purpose is so that the tab reloads when you set it to the same tab twice in a row
    setInboxTabState({ tab })
  }, [])

  usePusher(
    'message' + user.id,
    'rmt',
    useDebounceLeading(() => {
      setInboxTab(InboxTab.Inbox)
      if (conversationRef.current) conversationRef.current.tryReload()
    }, 15000)
  )

  const requireTermsOfService = useTermsOfService()

  const setComposeStateAfterTOS = async (composeState: ComposeState) => {
    if (await requireTermsOfService(user)) setComposeState(composeState)
  }

  const compose = () => {
    setComposeStateAfterTOS(DEFAULT_COMPOSE_STATE)
  }

  const onMessageSelected = React.useCallback(async (nm: MessageData) => {
    if (nm.draft && nm.type != 'phone') setComposeStateAfterTOS(await draftToComposeState(user, nm))
    else setConversationRequest((r) => (r?.messageID === nm.id ? undefined : { messageID: nm.id }))
  }, [])

  const navigate = useNavigate()

  const [preloadedRecipients, setPreloadedRecipients] = React.useState<
    (TeacherRecipient | StudentRecipient)[] | undefined
  >(undefined)
  const [preloadedSharedRecipients, setPreloadedSharedRecipients] = React.useState<SharedRecipient[] | undefined>(
    undefined
  )

  const newGroup = React.useCallback(() => {
    setEditGroupState(NEW_GROUP_STATE)
  }, [])
  const editGroup = React.useCallback((group: ContactGroup) => {
    setEditGroupState({
      group: group,
    })
  }, [])
  const deleteGroupCallback = React.useCallback(
    (g: ContactGroup) =>
      deleteGroup(g.id, setPreloadedRecipients).then((d) => {
        if (d) reloadContacts()
      }),
    [reloadContacts]
  )

  const [showFiltersOnMobile, setShowFiltersOnMobile] = React.useState(false)
  const isMobile = useIsMobile()
  return (
    <ThreePanelLayout
      blueAbovePanels={true}
      user={user}
      headerExtra={
        <>
          {showFiltersOnMobile || conversationRequest ? (
            <div className="flex-grow" />
          ) : isMobile ? (
            <FilterSearch simplified className="flex-grow sm:hidden" filters={filters} setFilters={setFilters} />
          ) : null}
          {!conversationRequest && (
            <Button
              className="ml-1 mr-3 sm:hidden"
              buttonStyle={ButtonStyle.ghostLight}
              shape={ButtonShape.iconSmall}
              onClick={() => setShowFiltersOnMobile((s) => !s)}
            >
              <IconFilter opacity={0.2} />
            </Button>
          )}
        </>
      }
      left={
        <>
          <Button onClick={compose} logo shape={ButtonShape.wideCentered} id="tutorial_compose">
            {tr('New Message')}
          </Button>
          <InboxTabs tab={inboxTab} setTab={setInboxTab} user={user} unreadCounts={unreadCounts} />
          <Contacts
            user={user}
            contacts={contacts}
            editGroup={editGroup}
            newGroup={newGroup}
            deleteGroup={deleteGroupCallback}
            filteredContacts={filteredContacts}
            setFilteredContacts={setFilteredContacts}
          />
          <div className="mt-auto flex flex-col gap-2">
            {state.language?.code !== 'en' && (
              <div className="cursor-pointer" onClick={() => changeLanguage('en')}>
                Switch to English
              </div>
            )}
            {user.hasMessagesFromLastYear && (
              <div className="flex items-center justify-between gap-2">
                <b>Showing</b>
                <Button
                  shape={ButtonShape.roundedSmall}
                  logo
                  onClick={() => setFilters({ ...filters, thisYearsMessages: !filters.thisYearsMessages })}
                >
                  {filters.thisYearsMessages ? "this year's messages" : 'all messages'}
                </Button>
              </div>
            )}
          </div>
        </>
      }
      right={
        conversationRequest ? (
          <Conversation
            ref={conversationRef}
            messageID={conversationRequest.messageID}
            messageUserID={conversationRequest.messageUserID}
            allSchoolMessages={filters.allSchoolMessages}
            showArchived={filters.showArchived}
            reloadUnreadCounts={reloadUnreadCounts}
          />
        ) : (
          <NoConversationTips />
        )
      }
      floating={
        <>
          {composeState && (
            <ComposeModal
              composeState={composeState}
              setComposeState={setComposeState}
              reloadContacts={reloadContacts}
              user={user}
              preloadedRecipients={preloadedRecipients}
              preloadedSharedRecipients={preloadedSharedRecipients}
              setPreloadedRecipients={setPreloadedRecipients}
              setPreloadedSharedRecipients={setPreloadedSharedRecipients}
              onSendComplete={(message) => {
                reloadUnreadCounts()
                const tabsToReload = ['later', 'schedule-video', 'schedule-interpreted'].includes(
                  message.sendOption.type
                )
                  ? [InboxTab.Scheduled]
                  : message.sendOption.type === 'draft'
                    ? [InboxTab.Draft]
                    : [InboxTab.Sent]
                if (message.draftID !== undefined) tabsToReload.push(InboxTab.Draft) // sent an existing draft
                if (tabsToReload.includes(inboxTab.tab)) setInboxTab(inboxTab.tab)
              }}
            />
          )}
          {editGroupState && (
            <EditGroupModal
              editGroupState={editGroupState}
              setEditGroupState={setEditGroupState}
              onSaved={reloadContacts}
              setPreloadedRecipients={setPreloadedRecipients}
            />
          )}
          <HomeOnboarding
            user={user}
            deleteGroup={async (gID) => {
              const deleted = await deleteGroup(gID, setPreloadedRecipients)
              if (deleted) reloadContacts()
              return deleted
            }}
            setComposeState={setComposeState}
          />
        </>
      }
      rightOpenMobile={!!conversationRequest}
      setRightOpenMobile={(open) => {
        if (!open) setConversationRequest(undefined)
      }}
    >
      {inboxTab.tab === InboxTab.Conference ? (
        user.type === 'teacher' ? (
          <Conferences user={user} inboxTab={inboxTab} />
        ) : (
          <ConferenceInvites user={user} inboxTab={inboxTab} />
        )
      ) : inboxTab.tab === InboxTab.Attendance ? (
        <AttendanceMessages
          filteredContacts={filteredContacts}
          selectedMessageID={conversationRequest?.messageID}
          messageSelected={onMessageSelected}
          inboxTab={inboxTab}
        />
      ) : inboxTab.tab === InboxTab.Survey ? (
        <Surveys user={user} inboxTab={inboxTab} />
      ) : inboxTab.tab === InboxTab.Assessment ? (
        <Assessments user={user} inboxTab={inboxTab} />
      ) : (
        <Messages
          user={user}
          filters={filters}
          setFilters={setFilters}
          filteredContacts={filteredContacts}
          tab={inboxTab}
          selectedMessageID={conversationRequest?.messageID}
          messageSelected={onMessageSelected}
          reloadUnreadCounts={reloadUnreadCounts}
          showFiltersOnMobile={showFiltersOnMobile}
        />
      )}
      <div className="z-10 flex items-center justify-center bg-slate-50 p-3 text-lg font-bold shadow-header sm:hidden">
        <Button onClick={compose} logo shape={ButtonShape.rounded} className="px-8">
          {tr('New Message')}
        </Button>
      </div>
    </ThreePanelLayout>
  )
}
